import React, { useState } from 'react';
import { View, Text, ScrollView, TextInput, TouchableOpacity, Alert, ActivityIndicator, RefreshControl, StyleSheet } from 'react-native';
import { Header } from '../../components/Header';
import { StatusBadge } from '../../components/StatusBadge';
import { useGetPendingMeetingsQuery, useApproveMeetingMutation, useRejectMeetingMutation } from '../../store/api';
import { useTheme } from '../../theme/ThemeContext';
import { COLORS, getThemeStyles, globalStyles } from '../../theme/designSystem';

export const PendingApprovalsScreen = () => {
  const { data: pendingMeetings = [], isLoading, refetch } = useGetPendingMeetingsQuery();
  const [approveApi, { isLoading: isApproving }] = useApproveMeetingMutation();
  const [rejectApi, { isLoading: isRejecting }] = useRejectMeetingMutation();
  const [selectedMeetingId, setSelectedMeetingId] = useState<string | null>(null);
  const [remarksMap, setRemarksMap] = useState<Record<string, string>>({});
  const { isDark } = useTheme();
  const theme = getThemeStyles(isDark);

  const handleApprove = async (id: string) => {
    try {
      setSelectedMeetingId(id);
      const remarks = remarksMap[id] || 'Approved by Master Admin';
      await approveApi({ id, adminRemarks: remarks }).unwrap();

      Alert.alert(
        'Meeting Approved! ✅',
        'Meeting status updated to CONFIRMED. WhatsApp message and Email confirmation have been automatically sent to the requested visitor.',
      );
      refetch();
    } catch (err: any) {
      Alert.alert('Approval Error', err?.data?.message || 'Failed to approve meeting.');
    } finally {
      setSelectedMeetingId(null);
    }
  };

  const handleReject = async (id: string) => {
    try {
      setSelectedMeetingId(id);
      const remarks = remarksMap[id] || 'Slot unavailable / Rejected by Admin';
      await rejectApi({ id, adminRemarks: remarks }).unwrap();

      Alert.alert('Meeting Rejected', 'Meeting status updated to REJECTED.');
      refetch();
    } catch (err: any) {
      Alert.alert('Rejection Error', err?.data?.message || 'Failed to reject meeting.');
    } finally {
      setSelectedMeetingId(null);
    }
  };

  return (
    <View style={[globalStyles.container, { backgroundColor: theme.bg }]}>
      <Header title="Pending Approvals" subtitle="Review visitor requests & trigger automated notifications" />

      <ScrollView
        style={{ flex: 1 }}
        contentContainerStyle={{ paddingHorizontal: 20, paddingBottom: 32 }}
        refreshControl={<RefreshControl refreshing={isLoading} onRefresh={refetch} />}
      >
        {pendingMeetings.length === 0 ? (
          <View style={[globalStyles.card, { backgroundColor: theme.cardBg, borderColor: theme.border, alignItems: 'center', marginVertical: 20 }]}>
            <Text style={{ fontSize: 40, marginBottom: 12 }}>🎉</Text>
            <Text style={[styles.emptyTitle, { color: theme.textPrimary }]}>
              All Pending Requests Cleared!
            </Text>
            <Text style={[styles.emptySubtitle, { color: theme.textSecondary }]}>
              There are no pending meeting approval requests awaiting your review.
            </Text>
          </View>
        ) : (
          pendingMeetings.map((meeting) => {
            const isProcessingThis = selectedMeetingId === meeting.id && (isApproving || isRejecting);
            return (
              <View
                key={meeting.id}
                style={[globalStyles.card, { backgroundColor: theme.cardBg, borderColor: theme.border, marginBottom: 16 }]}
              >
                <View style={styles.cardHeader}>
                  <View style={{ flex: 1, marginRight: 8 }}>
                    <Text style={[styles.visitorName, { color: theme.textPrimary }]}>
                      {meeting.visitorName}
                    </Text>
                    {meeting.visitorCompany && (
                      <Text style={[styles.companyName, { color: theme.textSecondary }]}>
                        {meeting.visitorCompany}
                      </Text>
                    )}
                  </View>
                  <StatusBadge status={meeting.status} />
                </View>

                {/* Details box */}
                <View style={[styles.detailsBox, { backgroundColor: isDark ? '#0F172A' : '#F1F5F9', borderColor: theme.border }]}>
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>📅 Date & Time:</Text>
                    <Text style={styles.detailValPrimary}>
                      {meeting.meetingDate} at {meeting.meetingTime}
                    </Text>
                  </View>
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>📞 Phone:</Text>
                    <Text style={[styles.detailVal, { color: theme.textPrimary }]}>{meeting.visitorPhone}</Text>
                  </View>
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>✉️ Email:</Text>
                    <Text style={[styles.detailVal, { color: theme.textPrimary }]}>{meeting.visitorEmail}</Text>
                  </View>
                  <View style={{ paddingTop: 6, borderTopWidth: 1, borderTopColor: 'rgba(148, 163, 184, 0.2)' }}>
                    <Text style={styles.detailLabel}>Purpose:</Text>
                    <Text style={[styles.purposeText, { color: theme.textPrimary }]}>
                      {meeting.meetingPurpose}
                    </Text>
                  </View>
                </View>

                {/* Remarks Input */}
                <Text style={[styles.label, { color: theme.textPrimary }]}>
                  Admin Remarks / Room Assignment (Optional)
                </Text>
                <TextInput
                  value={remarksMap[meeting.id] || ''}
                  onChangeText={(txt) => setRemarksMap((prev) => ({ ...prev, [meeting.id]: txt }))}
                  placeholder="e.g. Approved. Assigned Executive Boardroom 4."
                  placeholderTextColor={theme.textSecondary}
                  style={[
                    globalStyles.input,
                    {
                      backgroundColor: theme.inputBg,
                      borderColor: theme.border,
                      color: theme.textPrimary,
                      marginBottom: 16,
                    },
                  ]}
                />

                {/* Action Buttons */}
                <View style={styles.btnRow}>
                  <TouchableOpacity
                    onPress={() => handleApprove(meeting.id)}
                    disabled={isProcessingThis}
                    style={[styles.approveBtn, { backgroundColor: COLORS.success }]}
                  >
                    {isProcessingThis && isApproving ? (
                      <ActivityIndicator color="#fff" />
                    ) : (
                      <Text style={styles.btnText}>✓ Approve & Send Notifications</Text>
                    )}
                  </TouchableOpacity>

                  <TouchableOpacity
                    onPress={() => handleReject(meeting.id)}
                    disabled={isProcessingThis}
                    style={[styles.rejectBtn, { backgroundColor: COLORS.dangerBg, borderColor: COLORS.danger }]}
                  >
                    {isProcessingThis && isRejecting ? (
                      <ActivityIndicator color={COLORS.danger} />
                    ) : (
                      <Text style={[styles.btnText, { color: COLORS.danger }]}>✕ Reject</Text>
                    )}
                  </TouchableOpacity>
                </View>
              </View>
            );
          })
        )}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  emptyTitle: {
    fontSize: 16,
    fontWeight: '800',
  },
  emptySubtitle: {
    fontSize: 12,
    textAlign: 'center',
    marginTop: 4,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  visitorName: {
    fontSize: 17,
    fontWeight: '800',
  },
  companyName: {
    fontSize: 12,
  },
  detailsBox: {
    padding: 14,
    borderRadius: 14,
    borderWidth: 1,
    marginVertical: 10,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
  },
  detailLabel: {
    fontSize: 11,
    color: '#94A3B8',
    fontWeight: '600',
  },
  detailValPrimary: {
    fontSize: 11,
    fontWeight: '800',
    color: COLORS.primary,
  },
  detailVal: {
    fontSize: 11,
    fontWeight: '700',
  },
  purposeText: {
    fontSize: 11,
    marginTop: 2,
    lineHeight: 16,
  },
  label: {
    fontSize: 11,
    fontWeight: '700',
    marginBottom: 6,
  },
  btnRow: {
    flexDirection: 'row',
    gap: 10,
  },
  approveBtn: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rejectBtn: {
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  btnText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: '800',
  },
});
